/**
 * Google Maps configuration + helpers.
 *
 * Activated by REACT_APP_GOOGLE_MAPS_API_KEY. When unset, `isGoogleMapsConfigured`
 * is false and components fall back to plain form inputs / Leaflet maps.
 */

export const GOOGLE_MAPS_API_KEY: string =
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? "";

export const GOOGLE_MAPS_MAP_ID: string =
    process.env.REACT_APP_GOOGLE_MAPS_MAP_ID ?? "";

export function isGoogleMapsConfigured(): boolean {
    return GOOGLE_MAPS_API_KEY.trim().length > 0;
}

export type PlaceResult = {
    formatted: string;
    line1: string;
    line2: string;
    city: string;
    state: string;
    pincode: string;
    lat: number;
    lng: number;
};

/**
 * Reverse-geocode a lat/lng to a structured address via the Geocoding API.
 * Uses the browser fetch — key must be domain-restricted in production.
 */
export async function reverseGeocode(
    lat: number,
    lng: number,
): Promise<PlaceResult | null> {
    if (!isGoogleMapsConfigured()) return null;
    const url =
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}` +
        `&key=${encodeURIComponent(GOOGLE_MAPS_API_KEY)}`;
    try {
        const r = await fetch(url);
        const j = await r.json();
        if (j.status !== "OK" || !j.results?.length) return null;
        return normaliseGeocode(j.results[0], lat, lng);
    } catch {
        return null;
    }
}

type GeocodeComponent = {
    long_name: string;
    short_name: string;
    types: string[];
};

type GeocodeResult = {
    formatted_address: string;
    address_components: GeocodeComponent[];
    geometry?: { location?: { lat: number; lng: number } };
};

function pick(components: GeocodeComponent[], type: string, short = false): string {
    const c = components.find((x) => x.types.includes(type));
    return c ? (short ? c.short_name : c.long_name) : "";
}

export function normaliseGeocode(
    result: GeocodeResult,
    fallbackLat: number,
    fallbackLng: number,
): PlaceResult {
    const c = result.address_components ?? [];
    return {
        formatted: result.formatted_address ?? "",
        line1: [pick(c, "street_number"), pick(c, "route")].filter(Boolean).join(" "),
        line2: pick(c, "sublocality") || pick(c, "neighborhood"),
        city: pick(c, "locality") || pick(c, "administrative_area_level_2"),
        state: pick(c, "administrative_area_level_1"),
        pincode: pick(c, "postal_code"),
        lat: result.geometry?.location?.lat ?? fallbackLat,
        lng: result.geometry?.location?.lng ?? fallbackLng,
    };
}
